import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import {
  collectTuneNotationAssetIds,
  collectTuneAudioIds,
  normalizeTunePageMetadataAssetIds,
  tuneNotationLegacyImageUrl,
} from '../utils/tunePageMedia';
import NotationAssetImg from './NotationAssetImg';
import './PageViewer.css';
import './TunePage.css';
import './RelatedContentPreview.css';

const FINE_PRINT =
  "Many of the tunes posted on this site are Paul Cranford's initial drafts. " +
  'While many have already been featured in his published collections, others are ' +
  'currently being refined for future release through Cranford Publications.';

// Defaults must match backend tune-sections endpoint + template script
const SECTION_DEFS = [
  { key: 'products', label: '📚 Featured In',      defaultMode: 'explicit' },
  { key: 'articles', label: '📰 Related Articles',  defaultMode: 'off'      },
  { key: 'tunes',    label: '🎵 Related Tunes',     defaultMode: 'tags'     },
  { key: 'curated',  label: '🔗 Also See',          defaultMode: 'off'      },
];

const SECTION_TITLE_DEFAULTS = {
  products: '📚 Featured In',
  articles: '📰 Related Articles',
  tunes: '🎵 Related Tunes',
  curated: '🔗 Also See',
};

const CURATED_ICONS = {
  tune: '🎵',
  article: '📰',
  product: '📚',
  route: '🔗',
  'tune-asset': '🎼',
};
const CURATED_LABELS = {
  tune: 'Tune',
  article: 'Page',
  product: 'Book / Recording',
  route: 'Link',
  'tune-asset': 'ABC',
};

// ── tiny card components ────────────────────────────────────────────────────

const ProductCard = ({ item }) => {
  const [imgErr, setImgErr] = useState(false);
  const imgSrc = item.image || null;

  return (
    <div style={{
      border: '1px solid var(--color-border)',
      borderRadius: '8px',
      overflow: 'hidden',
      background: 'var(--color-surface)',
      display: 'flex',
      flexDirection: 'column',
      fontSize: '0.85rem',
    }}>
      {imgSrc && !imgErr && (
        <img
          src={imgSrc}
          alt={item.name}
          onError={() => setImgErr(true)}
          style={{ width: '100%', height: '100px', objectFit: 'cover' }}
        />
      )}
      <div style={{ padding: '8px 10px', flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: '2px', lineHeight: 1.3 }}>{item.name}</div>
        {item.sku && <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>{item.sku}</div>}
        {item.price_cents != null && (
          <div style={{ color: 'var(--color-primary)', fontWeight: 600, marginTop: '4px' }}>
            ${(item.price_cents / 100).toFixed(2)}
          </div>
        )}
      </div>
    </div>
  );
};

const PageCard = ({ item, badge }) => {
  const card = (
    <div style={{
      border: '1px solid var(--color-border)',
      borderRadius: '8px',
      padding: '10px 12px',
      background: 'var(--color-surface)',
      fontSize: '0.85rem',
    }}>
      {badge && (
        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--color-primary)', fontWeight: 700, marginBottom: '4px' }}>
          {badge}
        </div>
      )}
      <div style={{ fontWeight: 600, lineHeight: 1.3 }}>{item.title || item.page_id}</div>
      {item.href && item._type === 'route' && (
        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.72rem', marginTop: '4px' }}>{item.href}</div>
      )}
      {item.description && (
        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.78rem', marginTop: '3px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {item.description}
        </div>
      )}
    </div>
  );
  if (item.href && (item._type === 'route' || item._type === 'tune-asset')) {
    return (
      <a href={item.href} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
        {card}
      </a>
    );
  }
  return card;
};

const ModeBadge = ({ mode }) => (
  <span className={`rc-mode-badge rc-mode-badge--${mode || 'off'}`}>
    {mode}
  </span>
);

/** Featured In — cover image only (name on link title / aria-label). */
const ProductCoverOnly = ({ item }) => {
  const [imgErr, setImgErr] = useState(false);
  const imgSrc = item.image || null;

  return (
    <div className="related-product-card related-product-card--cover-only">
      <a
        href={`/product/${item.id}`}
        className="related-product-link related-product-link--cover-only"
        title={item.name || ''}
        aria-label={item.name ? `View product: ${item.name}` : 'View product'}
      >
        {imgSrc && !imgErr ? (
          <div className="related-product-image">
            <img
              src={imgSrc}
              alt=""
              loading="lazy"
              onError={() => setImgErr(true)}
            />
          </div>
        ) : (
          <div className="related-product-image related-product-image--empty" aria-hidden="true">
            <span className="related-product-fallback">📚</span>
          </div>
        )}
      </a>
    </div>
  );
};

const parsePageMeta = (page) => {
  const m = page.metadata;
  if (!m) return {};
  if (typeof m === 'object') return m;
  try {
    return JSON.parse(m);
  } catch {
    return {};
  }
};

const TuneEditorCard = ({ item }) => {
  const href = `/page/${item.page_id}`;
  const meta = parsePageMeta(item);
  const region = item.region || meta.region || 'Traditional';
  const tuneType = item.tune_type || meta.tune_type || 'Tune';
  const album = item.album || meta.album;
  const rawComposer = item.composer ?? meta.composer;
  const composer =
    rawComposer == null || rawComposer === ''
      ? ''
      : Array.isArray(rawComposer)
        ? rawComposer.map((x) => String(x).trim()).filter(Boolean).join(', ')
        : String(rawComposer).trim();
  let tagsArr = [];
  if (typeof item.tags === 'string') {
    tagsArr = item.tags.split(',').map((t) => t.trim()).filter(Boolean);
  } else if (Array.isArray(item.tags)) {
    tagsArr = item.tags.map(String);
  }

  return (
    <a href={href} className="related-tune-card">
      <div className="tune-card-header">
        <h3 className="tune-card-title">{item.title || item.page_id}</h3>
      </div>
      {composer ? <p className="tune-card-composer">{composer}</p> : null}
      <p className="tune-card-meta">
        {region} • {tuneType}
      </p>
      {album ? <p className="tune-card-album">💿 {album}</p> : null}
      {tagsArr.length > 0 ? (
        <div className="tune-card-tags">
          {tagsArr.slice(0, 4).map((t) => (
            <span key={t} className="tune-card-tag">{t}</span>
          ))}
        </div>
      ) : null}
    </a>
  );
};

const CuratedEditorCard = ({ item }) => {
  let href;
  let title;
  let desc = '';
  let typeKey = 'article';

  if (item._type === 'product') {
    typeKey = 'product';
    href = `/product/${item.id}`;
    title = item.name || `Product #${item.id}`;
  } else if (item._type === 'route') {
    typeKey = 'route';
    href = item.href || '#';
    title = item.title || item.href || 'Link';
  } else if (item._type === 'tune-asset') {
    typeKey = 'tune-asset';
    href = item.href || `/tune-asset/${item.page_id}`;
    title = item.title || `ABC #${item.page_id}`;
  } else {
    href = item.url || `/page/${item.page_id}`;
    title = item.title || item.page_id;
    desc = (item.description || '').slice(0, 120);
    typeKey =
      item.page_type === 'tune-notation' || item.page_type === 'abc-notation'
        ? 'tune'
        : 'article';
  }

  const icon = CURATED_ICONS[typeKey] || '🔗';
  const label = CURATED_LABELS[typeKey] || typeKey;

  return (
    <div className="curated-card">
      <a href={href}>
        <span className="curated-card-icon">{icon}</span>
        <div className="curated-card-body">
          <div className="curated-card-title">{title}</div>
          {desc ? <div className="curated-card-desc">{desc}</div> : null}
        </div>
        <span className="curated-card-type">{label}</span>
      </a>
    </div>
  );
};

const sectionShellClass = (key) =>
  ({
    products: 'related-products-section',
    articles: 'related-articles-section',
    tunes: 'related-tunes',
    curated: 'curated-section',
  }[key] || 'related-products-section');

/** Same section structure & class names as the tune page HTML template, filled with live data. */
const TuneTemplateSection = ({ def, mode, items, loading, heading }) => {
  const displayTitle = heading || def.label;
  const shell = sectionShellClass(def.key);

  const header = (
    <h2 className="tune-section-header">
      <span className="tune-section-header-title">{displayTitle}</span>
      {mode !== 'off' ? <ModeBadge mode={mode} /> : <ModeBadge mode="off" />}
      {loading ? (
        <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>loading…</span>
      ) : null}
    </h2>
  );

  if (mode === 'off') {
    return (
      <div className={`${shell} editor-tune-section--off`}>
        {header}
        <p className="editor-tune-section-hint">
          This block is turned off — it will not appear on the published tune page.
        </p>
      </div>
    );
  }

  const emptyPlaceholder = !loading && items.length === 0 ? (
    <div className="related-placeholder">
      <p style={{ margin: 0 }}>No items yet — the live page hides this section when empty.</p>
    </div>
  ) : null;

  let body = null;
  if (def.key === 'products') {
    body = (
      <div className="related-products-grid">
        {items.map((item, i) => (
          <ProductCoverOnly key={item.id ?? i} item={item} />
        ))}
        {emptyPlaceholder}
      </div>
    );
  } else if (def.key === 'articles') {
    body = (
      <div className="related-articles-list">
        {items.map((item, i) => {
          const url = item.url || `/page/${item.page_id}`;
          const rawDesc = item.description || '';
          const desc = rawDesc.slice(0, 140);
          return (
            <div key={item.page_id ?? i} className="related-article-card">
              <a href={url} className="related-article-link">
                <div className="related-article-title">{item.title || item.page_id}</div>
                {desc ? (
                  <div className="related-article-desc">
                    {desc}
                    {rawDesc.length > 140 ? '…' : ''}
                  </div>
                ) : null}
              </a>
            </div>
          );
        })}
        {emptyPlaceholder}
      </div>
    );
  } else if (def.key === 'tunes') {
    body = (
      <div className="related-content-grid">
        {items.map((item, i) => (
          <TuneEditorCard key={item.page_id ?? i} item={item} />
        ))}
        {emptyPlaceholder}
      </div>
    );
  } else {
    body = (
      <div className="curated-grid">
        {items.map((item, i) => (
          <CuratedEditorCard key={item.id ?? item.page_id ?? item.href ?? i} item={item} />
        ))}
        {emptyPlaceholder}
      </div>
    );
  }

  return (
    <div className={shell}>
      {header}
      {body}
    </div>
  );
};

// ── data fetching helpers ────────────────────────────────────────────────────

const fetchProductById = (id) =>
  fetch(`/api/products/${id}`).then(r => r.ok ? r.json() : null).catch(() => null);

const fetchPagesByIds = (ids) => {
  if (!ids.length) return Promise.resolve([]);
  return fetch(`/api/content/pages-by-ids?ids=${ids.join(',')}`)
    .then(r => r.ok ? r.json() : []).catch(() => []);
};

const fetchRelated = (tags, contentType, excludeId, limit = 6) => {
  const params = new URLSearchParams({ tags, limit });
  if (contentType) params.set('content_type', contentType);
  if (excludeId) params.set('exclude', excludeId);
  return fetch(`/api/content/related?${params}`).then(r => r.ok ? r.json() : []).catch(() => []);
};

const fetchProductsByTags = (tags, limit = 6) =>
  fetch(`/api/products/related?tags=${encodeURIComponent(tags)}&limit=${limit}`)
    .then(r => r.ok ? r.json() : []).catch(() => []);

// ── main component ────────────────────────────────────────────────────────────

const DEFAULT_SECTION_LIMITS = { products: 24, articles: 8, tunes: 12, curated: 24 };

const TunePagePreview = ({ metadata, pageId, title, description, refreshKey }) => {
  const [sectionData, setSectionData] = useState({ products: [], articles: [], tunes: [], curated: [] });
  const [loading, setLoading] = useState({ products: false, articles: false, tunes: false, curated: false });
  const [cranfordNotationFallback, setCranfordNotationFallback] = useState('');

  const metaObj = useMemo(() => {
    if (metadata == null) return {};
    if (typeof metadata === 'string') {
      try {
        return JSON.parse(metadata);
      } catch {
        return {};
      }
    }
    return metadata;
  }, [metadata]);
  const rc = metaObj.related_content || {};
  const sections = rc.sections || {};
  const sectionLimits = rc.section_limits || {};
  const rawSectionTitles = rc.section_titles || {};
  const headingFor = (key) => {
    const c = rawSectionTitles[key];
    if (c != null && String(c).trim()) return String(c).trim().slice(0, 200);
    return SECTION_TITLE_DEFAULTS[key] || SECTION_DEFS.find(d => d.key === key)?.label;
  };
  const capFor = (key) => {
    const n = parseInt(sectionLimits[key], 10);
    if (Number.isFinite(n) && n >= 1) return Math.min(100, n);
    return DEFAULT_SECTION_LIMITS[key] ?? 8;
  };
  const tags = (() => {
    const raw = metaObj.tags || '';
    return typeof raw === 'string' ? raw : Array.isArray(raw) ? raw.join(',') : '';
  })();

  const getMode = (key) => {
    const v = sections[key];
    if (v && typeof v === 'object' && v.mode != null) return v.mode;
    if (typeof v === 'string') return v;
    return SECTION_DEFS.find(d => d.key === key)?.defaultMode ?? 'off';
  };

  const setSection = (key, items) => setSectionData(prev => ({ ...prev, [key]: items }));
  const setLoad = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

  // ── products ──
  useEffect(() => {
    const mode = getMode('products');
    if (mode === 'off') { setSection('products', []); return; }
    setLoad('products', true);

    const go = async () => {
      const idSet = new Set();
      // from product_ids field
      const rawIds = (typeof metadata === 'string' ? JSON.parse(metadata) : (metadata || {})).product_ids || '';
      String(rawIds).split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)).forEach(id => idSet.add(id));
      // from explicit related_content.products
      if (mode === 'explicit' || mode === 'both') {
        (rc.products || []).forEach(p => idSet.has(p.id) || idSet.add(p.id));
      }

      let items = [];
      if (idSet.size > 0) {
        const results = await Promise.all([...idSet].map(fetchProductById));
        items = results.filter(Boolean);
      }
      if ((mode === 'tags' || mode === 'both') && tags) {
        const maxP = capFor('products');
        const tagBased = await fetchProductsByTags(tags, Math.max(maxP, 8));
        const existing = new Set(items.map(p => p.id));
        tagBased.filter(p => !existing.has(p.id)).forEach(p => items.push(p));
      }
      setSection('products', items.slice(0, capFor('products')));
      setLoad('products', false);
    };
    go().catch(() => setLoad('products', false));
  }, [metadata, pageId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── articles ──
  useEffect(() => {
    const mode = getMode('articles');
    if (mode === 'off') { setSection('articles', []); return; }
    setLoad('articles', true);

    const go = async () => {
      let items = [];
      if (mode === 'explicit' || mode === 'both') {
        const ids = (rc.articles || []).map(a => a.id).filter(Boolean);
        if (ids.length) {
          const pages = await fetchPagesByIds(ids);
          items = pages;
        }
      }
      if ((mode === 'tags' || mode === 'both') && tags) {
        const maxA = capFor('articles');
        const tagBased = await fetchRelated(tags, 'article', pageId, Math.max(maxA, 8));
        const existing = new Set(items.map(p => p.page_id));
        tagBased.filter(p => !existing.has(p.page_id)).forEach(p => items.push(p));
      }
      setSection('articles', items.slice(0, capFor('articles')));
      setLoad('articles', false);
    };
    go().catch(() => setLoad('articles', false));
  }, [metadata, pageId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── tunes ──
  useEffect(() => {
    const mode = getMode('tunes');
    if (mode === 'off') { setSection('tunes', []); return; }
    setLoad('tunes', true);

    const go = async () => {
      let items = [];
      if (mode === 'explicit' || mode === 'both') {
        const ids = (rc.tunes || []).map(t => t.id).filter(Boolean);
        if (ids.length) {
          const pages = await fetchPagesByIds(ids);
          items = pages;
        }
      }
      if ((mode === 'tags' || mode === 'both') && pageId) {
        // Same endpoint as TunePage.js; source_tags = editor metadata so preview matches unsaved tag edits
        const maxT = capFor('tunes');
        const rawTags = metaObj.tags;
        const tagsStr = typeof rawTags === 'string'
          ? rawTags.trim()
          : (Array.isArray(rawTags) ? rawTags.map(String).join(',') : '');
        const params = new URLSearchParams({ limit: String(maxT) });
        if (tagsStr) params.set('source_tags', tagsStr.slice(0, 800));
        const similar = await fetch(`/api/content/tunes/related/${pageId}?${params}`)
          .then(r => r.ok ? r.json() : []).catch(() => []);
        const existing = new Set(items.map(p => p.page_id));
        similar.filter(p => !existing.has(p.page_id)).forEach(p => items.push(p));
      }
      setSection('tunes', items.slice(0, capFor('tunes')));
      setLoad('tunes', false);
    };
    go().catch(() => setLoad('tunes', false));
  }, [metadata, pageId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── curated ──
  useEffect(() => {
    const mode = getMode('curated');
    if (mode === 'off') { setSection('curated', []); return; }
    setLoad('curated', true);

    const go = async () => {
      const curatedList = rc.curated || [];
      if (!curatedList.length) { setSection('curated', []); setLoad('curated', false); return; }

      const productIds = curatedList.filter(i => i.type === 'product').map(i => i.id);
      const pageIds = curatedList
        .filter(i => i.type !== 'product' && i.type !== 'route' && i.type !== 'tune-asset')
        .map(i => String(i.id));

      const [prods, pages] = await Promise.all([
        Promise.all(productIds.map(fetchProductById)).then(r => r.filter(Boolean).map(p => ({ ...p, _type: 'product' }))),
        fetchPagesByIds(pageIds),
      ]);

      const ordered = curatedList.map(ci => {
        if (ci.type === 'product') return prods.find(p => p.id === ci.id);
        if (ci.type === 'route' && ci.href) {
          return {
            title: ci.title || ci.href,
            description: '',
            page_type: 'site-link',
            page_id: ci.href,
            _type: 'route',
            href: ci.href,
          };
        }
        if (ci.type === 'tune-asset' && ci.id != null) {
          return {
            title: ci.title || `ABC #${ci.id}`,
            description: '',
            page_type: 'abc-asset',
            page_id: String(ci.id),
            _type: 'tune-asset',
            href: `/tune-asset/${ci.id}`,
          };
        }
        return pages.find(p => String(p.page_id) === String(ci.id));
      }).filter(Boolean);

      setSection('curated', ordered.slice(0, capFor('curated')));
      setLoad('curated', false);
    };
    go().catch(() => setLoad('curated', false));
  }, [metadata, pageId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const metaForMedia =
    metaObj && typeof metaObj === 'object' ? { ...metaObj } : {};
  normalizeTunePageMetadataAssetIds(metaForMedia);
  const notationIds = collectTuneNotationAssetIds(metaForMedia);
  const notationLegacyUrl = tuneNotationLegacyImageUrl(metaForMedia);
  const audioIds = collectTuneAudioIds(metaForMedia);
  const sourceUrl =
    metaObj.source_url || metaObj.source || metaObj.source_reference || '';
  const displayTitle = title ? decodeHtmlEntities(title) : '';

  const notationFallbackUrl = notationLegacyUrl || cranfordNotationFallback;

  const cranfordSourceUrlForScrape = useMemo(() => {
    const ok = (u) =>
      u &&
      /^https?:\/\//i.test(String(u)) &&
      /cranfordpub\.com/i.test(String(u));
    const raw = [metaObj.source_url, metaObj.source, metaObj.source_reference].find(ok);
    return raw ? String(raw).trim() : '';
  }, [metaObj]);

  useEffect(() => {
    setCranfordNotationFallback('');
    if (!cranfordSourceUrlForScrape) return undefined;
    if (notationLegacyUrl) return undefined;
    let cancelled = false;
    fetch(
      `/api/content/notation-from-source?url=${encodeURIComponent(cranfordSourceUrlForScrape)}`
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled && j && typeof j.url === 'string' && j.url) {
          setCranfordNotationFallback(j.url);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [pageId, cranfordSourceUrlForScrape, notationLegacyUrl, refreshKey]);

  return (
    <div className="page-editor-tune-template-preview tune-page">

      {/* ── Nav bar ── */}
      <div className="tune-nav-bar">
        {sourceUrl ? (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="nav-link-original">
            📎 View Original Page
          </a>
        ) : <span />}
        <span className="nav-link-back" style={{ opacity: 0.5, cursor: 'default' }}>← Back to Tunes</span>
      </div>

      {/* ── Title ── */}
      {displayTitle && (
        <div className="tune-header">
          <h1 className="tune-title">{displayTitle}</h1>
        </div>
      )}

      {/* ── Notation images ── */}
      {(notationIds.length > 0 || notationFallbackUrl) && (
        <div className="tune-notation">
          {notationIds.map((id) => (
            <NotationAssetImg
              key={id}
              assetId={id}
              title={displayTitle}
              fallbackUrl={notationFallbackUrl || ''}
            />
          ))}
          {notationIds.length === 0 && notationFallbackUrl ? (
            <img
              src={notationFallbackUrl}
              alt={`${displayTitle} musical notation`}
              onError={(e) => {
                const el = e.currentTarget.closest('.tune-notation');
                if (el) el.style.display = 'none';
              }}
            />
          ) : null}
        </div>
      )}

      {/* ── Audio ── */}
      {audioIds.length > 0 && (
        <div className="tune-audio">
          {audioIds.map((id) => (
            <audio key={id} controls preload="metadata">
              <source src={`/api/assets/${id}/download`} />
              Your browser does not support the audio element.
            </audio>
          ))}
        </div>
      )}

      {/* ── About This Tune ── */}
      {description && (
        <div className="tune-description">
          <h2 className="tune-section-header">📝 About This Tune</h2>
          <div
            className="tune-description-text"
            dangerouslySetInnerHTML={{ __html: description }}
          />
          <p className="tune-fine-print">{FINE_PRINT}</p>
        </div>
      )}

      {/* ── Related sections ── */}
      {SECTION_DEFS.map((def) => (
        <TuneTemplateSection
          key={def.key}
          def={def}
          mode={getMode(def.key)}
          items={sectionData[def.key]}
          loading={loading[def.key]}
          heading={headingFor(def.key)}
        />
      ))}
    </div>
  );
};

// ── simple flat preview for non-tune pages ────────────────────────────────────

const SimplePreview = ({ metadata, pageId, title, description, html }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const getTags = useCallback(() => {
    const m = typeof metadata === 'string' ? JSON.parse(metadata) : (metadata || {});
    if (m.tags) return typeof m.tags === 'string' ? m.tags : m.tags.join(',');
    const text = (html || '').replace(/<[^>]*>/g, ' ').toLowerCase();
    const allText = `${title} ${description} ${text}`.toLowerCase();
    const kw = ['fiddle','reel','jig','strathspey','cape breton','scottish','irish','music','biography','history'];
    const found = kw.filter(k => allText.includes(k));
    return found.length ? found.join(',') : 'Article';
  }, [metadata, html, title, description]);

  useEffect(() => {
    const go = async () => {
      setLoading(true);
      try {
        const m = typeof metadata === 'string' ? JSON.parse(metadata) : (metadata || {});
        const rc = m.related_content || {};
        const maxItems = rc.max_items != null ? parseInt(rc.max_items, 10) : 10;
        if (maxItems === 0) { setItems([]); setLoading(false); return; }

        const tags = getTags();
        const [prods, content] = await Promise.all([
          fetch(`/api/products/related?tags=${encodeURIComponent(tags)}&limit=10`).then(r => r.ok ? r.json() : []).catch(() => []),
          fetchRelated(tags, null, pageId, 10),
        ]);
        const combined = [
          ...prods.map(p => ({ ...p, _type: 'product' })),
          ...content.map(c => ({ ...c, _type: c.page_type === 'tune-notation' ? 'tune' : 'article' })),
        ].sort((a, b) => (b.score || b.match_score || 0) - (a.score || a.match_score || 0)).slice(0, maxItems);
        setItems(combined);
      } catch (e) {
        console.error('Preview error', e);
      }
      setLoading(false);
    };
    go();
  }, [metadata, pageId, getTags]);

  if (loading) return <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Loading…</div>;
  if (!items.length) return <p style={{ color: 'var(--color-text-secondary)', fontStyle: 'italic', fontSize: '0.85rem' }}>No related items matched.</p>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
      {items.map((item, i) => {
        if (item._type === 'product') return <ProductCard key={item.id ?? i} item={item} />;
        return <PageCard key={item.page_id ?? i} item={item} badge={item._type} />;
      })}
    </div>
  );
};

// ── exported component ────────────────────────────────────────────────────────

const RelatedContentPreview = ({ metadata, pageId, title, description, html, pageType, refreshKey }) => {
  const isTune = pageType === 'tune-notation' || pageType === 'abc-notation';

  return (
    <div className="page-editor-related-preview-wrap">
      {!isTune ? (
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: 'var(--color-text-primary)' }}>
          Related content preview
        </h3>
      ) : null}

      {isTune ? (
        <TunePagePreview
          metadata={metadata}
          pageId={pageId}
          title={title}
          description={description}
          refreshKey={refreshKey}
        />
      ) : (
        <SimplePreview
          metadata={metadata}
          pageId={pageId}
          title={title}
          description={description}
          html={html}
        />
      )}
    </div>
  );
};

export default RelatedContentPreview;
